import React, { useState, useContext, useEffect } from 'react';
import {
  Box, Container, Heading, FormControl, FormLabel, Input,
  Button, VStack, Text, useToast, Select
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [adminEmail, setAdminEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const { login } = useContext(AuthContext);
  const { themeData } = useContext(ThemeContext);
  const navigate = useNavigate();
  const toast = useToast();

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const accentColor = themeData?.colors?.accent || '#63B3ED';
  const backgroundColor = themeData?.colors?.background || '#F7FAFC';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';

  // Theme-aware colors
  const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
  const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
  const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
  const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
  const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
  const inputBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white';
  const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.300';
  const focusBorderColor = isDarkTheme ? accentColor : primaryColor;
  const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

  const isProduction = process.env.NODE_ENV === "production";
  const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

  // Extract subdomain from hostname
  useEffect(() => {
    const extractSubdomain = () => {
      if (!isProduction) {
        // In development, use 'samplefour' as the subdomain
        return 'samplefour';
      }

      const hostname = window.location.hostname;
      // Examples: hdfc.finansee.eu -> hdfc, sbi.finansee.eu -> sbi
      const parts = hostname.split('.');
      
      // If hostname has at least 3 parts (subdomain.domain.tld), extract the first part
      if (parts.length >= 3) {
        return parts[0];
      }
      
      // Fallback to the full hostname if structure is unexpected
      return hostname;
    };

    setSubdomain(extractSubdomain());
  }, [isProduction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;

      if (role === 'admin') {
        // Use the subdomain-specific login endpoint for admin
        response = await axios.post(`${API_BASE_URL}/auth/login-subdomain`, {
          email,
          password,
          subdomain
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Use the subdomain-specific login-others endpoint for other roles
        if (!adminEmail) {
          toast({
            title: 'Admin Email Required',
            description: 'Please enter the admin email for non-admin roles',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setLoading(false);
          return;
        }

        response = await axios.post(`${API_BASE_URL}/auth/login-others-subdomain`, {
          role,
          email,
          password,
          adminEmail,
          subdomain
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      const data = response.data;

      if (data.token) {
        login(data.token);
        toast({
          title: 'Login successful',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/dashboard');
      } else {
        toast({
          title: 'Login failed',
          description: data.message || 'Invalid credentials',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      let errorMessage = 'Something went wrong. Please try again.';

      if (error.response) {
        // Server responded with an error status
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please check your connection.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
      <Navbar />

      <Container maxW="md" flex="1" py={20}>
        <Box 
          bg={cardBg} 
          p={8} 
          borderRadius="xl" 
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Heading 
            as="h2" 
            size="xl" 
            mb={2} 
            textAlign="center" 
            color={headingTextColor}
          >
            Login to FinanSee
          </Heading>
          
          <Text 
            fontSize="sm" 
            color={mutedTextColor} 
            textAlign="center" 
            mb={6}
          >
            Access your financial insights dashboard
          </Text>

          {/* Optional: Display the current subdomain for debugging */}
          {!isProduction && subdomain && (
            <Box 
              mb={4} 
              p={3} 
              bg={isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'blue.50'} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Text fontSize="sm" color={bodyTextColor} textAlign="center">
                <Text as="span" fontWeight="bold">Development Mode:</Text> Subdomain: {subdomain}
              </Text>
            </Box>
          )}

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={labelTextColor} fontWeight="semibold" fontSize="sm">
                  Role
                </FormLabel>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Select your role"
                  bg={inputBg}
                  color={headingTextColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: focusBorderColor }}
                  _focus={{ 
                    borderColor: focusBorderColor, 
                    boxShadow: `0 0 0 1px ${focusBorderColor}` 
                  }}
                  size="lg"
                >
                  <option value="admin" style={{ background: isDarkTheme ? '#2D3748' : 'white' }}>
                    Admin
                  </option>
                  <option value="rm" style={{ background: isDarkTheme ? '#2D3748' : 'white' }}>
                    RM
                  </option>
                  <option value="compliance">Compliance</option>
                  <option value="head">Head</option>
                  <option value="view_only">View</option>
                  <option value="portfolio_adviser">Portfolio Adviser</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={labelTextColor} fontWeight="semibold" fontSize="sm">
                  Email
                </FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  bg={inputBg}
                  color={headingTextColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: focusBorderColor }}
                  _focus={{ 
                    borderColor: focusBorderColor, 
                    boxShadow: `0 0 0 1px ${focusBorderColor}` 
                  }}
                  _placeholder={{ color: mutedTextColor }}
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={labelTextColor} fontWeight="semibold" fontSize="sm">
                  Password
                </FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  bg={inputBg}
                  color={headingTextColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: focusBorderColor }}
                  _focus={{ 
                    borderColor: focusBorderColor, 
                    boxShadow: `0 0 0 1px ${focusBorderColor}` 
                  }}
                  _placeholder={{ color: mutedTextColor }}
                  size="lg"
                />
              </FormControl>

              {role !== 'admin' && (
                <FormControl isRequired>
                  <FormLabel color={labelTextColor} fontWeight="semibold" fontSize="sm">
                    Admin Email
                  </FormLabel>
                  <Input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@email.com"
                    bg={inputBg}
                    color={headingTextColor}
                    borderColor={borderColor}
                    _hover={{ borderColor: focusBorderColor }}
                    _focus={{ 
                      borderColor: focusBorderColor, 
                      boxShadow: `0 0 0 1px ${focusBorderColor}` 
                    }}
                    _placeholder={{ color: mutedTextColor }}
                    size="lg"
                  />
                </FormControl>
              )}

              <Button
                type="submit"
                bg={primaryColor}
                color="white"
                width="full"
                mt={4}
                size="lg"
                isLoading={loading}
                loadingText="Logging in..."
                _hover={{ bg: buttonHoverBg }}
                _active={{ bg: secondaryColor }}
              >
                Login
              </Button>

              {/* <Text mt={4}>
                Don't have an account?{' '}
                <ChakraLink as={Link} to="/register" color="blue.600">
                  Register here
                </ChakraLink>
              </Text> */}
            </VStack>
          </form>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default LoginPage;